/**
 * Supabase 认证工具函数
 */
import { supabase } from '@/lib/supabase/client';
import { Message } from '@arco-design/web-react';
import type { User } from '@supabase/supabase-js';
import { isJwtTokenFresh } from './jwt';

export interface SignInParams {
  email: string;
  password: string;
}

export interface SignUpParams {
  email: string;
  password: string;
  name?: string;
}

const ACCESS_TOKEN_CACHE_TTL_MS = 60 * 1000;
let cachedAccessToken: string | null = null;
let cachedAccessTokenExpireAt = 0;
let accessTokenPromise: Promise<string | null> | null = null;

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function setAccessTokenCache(token: string | null): void {
  cachedAccessToken = token;
  cachedAccessTokenExpireAt = token
    ? Date.now() + ACCESS_TOKEN_CACHE_TTL_MS
    : 0;
}

function readTokenFromStorage(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const token = localStorage.getItem('token');
  if (!isJwtTokenFresh(token)) {
    if (token) {
      localStorage.removeItem('token');
    }
    return null;
  }

  return token;
}

/**
 * 登录
 */
export async function signIn(params: SignInParams) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: params.email,
      password: params.password,
    });

    if (error) {
      throw error;
    }

    if (data.user) {
      // 保存用户信息到 localStorage
      localStorage.setItem('userStatus', 'login');
      localStorage.setItem('userId', data.user.id);
      localStorage.setItem('userEmail', data.user.email || '');

      // 保存 session token（用于 API 请求）
      if (data.session?.access_token) {
        localStorage.setItem('token', data.session.access_token);
        setAccessTokenCache(data.session.access_token);
      }

      return {
        success: true,
        user: data.user,
        session: data.session,
      };
    }

    throw new Error('登录失败：未返回用户信息');
  } catch (error: unknown) {
    console.error('登录错误:', error);
    const errorMessage = getErrorMessage(error, '登录失败，请检查邮箱和密码是否正确');
    Message.error(errorMessage);
    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * 登出
 */
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      throw error;
    }

    // 清除本地存储
    localStorage.removeItem('userStatus');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('token');
    setAccessTokenCache(null);

    return {
      success: true,
    };
  } catch (error: unknown) {
    console.error('登出错误:', error);
    const errorMessage = getErrorMessage(error, '登出失败');
    Message.error(errorMessage);
    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * 获取当前用户
 */
export async function getCurrentUser() {
  try {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error) {
      throw error;
    }

    return user;
  } catch (error: unknown) {
    console.error('获取用户错误:', error);
    return null;
  }
}

/**
 * 获取当前 session
 */
export async function getCurrentSession() {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      throw error;
    }

    return session;
  } catch (error: unknown) {
    console.error('获取 session 错误:', error);
    return null;
  }
}

/**
 * 获取访问令牌
 */
export async function getAccessToken(): Promise<string | null> {
  const localToken = readTokenFromStorage();
  if (localToken) {
    setAccessTokenCache(localToken);
    return localToken;
  }

  if (
    cachedAccessToken &&
    cachedAccessTokenExpireAt > Date.now() &&
    isJwtTokenFresh(cachedAccessToken)
  ) {
    return cachedAccessToken;
  }

  if (accessTokenPromise) {
    return accessTokenPromise;
  }

  accessTokenPromise = (async () => {
    try {
      const session = await getCurrentSession();
      const token = session?.access_token || null;
      if (token) {
        localStorage.setItem('token', token);
      }
      setAccessTokenCache(token);
      return token;
    } catch (error) {
      console.error('获取访问令牌失败:', error);
      return null;
    } finally {
      accessTokenPromise = null;
    }
  })();

  try {
    return await accessTokenPromise;
  } catch (error) {
    console.error('获取访问令牌失败:', error);
    return null;
  }
}

/**
 * 监听认证状态变化
 */
export function onAuthStateChange(callback: (user: User | null) => void) {
  return supabase.auth.onAuthStateChange((event, session) => {
    if (session?.user) {
      localStorage.setItem('userStatus', 'login');
      localStorage.setItem('userId', session.user.id);
      localStorage.setItem('userEmail', session.user.email || '');
      if (session.access_token) {
        localStorage.setItem('token', session.access_token);
        setAccessTokenCache(session.access_token);
      }
      callback(session.user);
    } else {
      localStorage.removeItem('userStatus');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('token');
      setAccessTokenCache(null);
      callback(null);
    }
  });
}
